import queryString from "query-string";
import { type RouteComponentProps } from "react-router-dom";

import { lazyLoadWithRetry } from "utilities/react.utilities";
import { routes } from "utilities/routes";

import { BTSuspense } from "commonComponents/btWrappers/BTSuspense/BTSuspense";
import { RouteRelative } from "commonComponents/utilities/RouteRelative/RouteRelative";
import { ShowOnPortal } from "commonComponents/utilities/ShowOnPortal/ShowOnPortal";

import { type IBillProps } from "entity/bill/Bill/Bill";
import { getInitialBillDocumentType } from "entity/bill/common/getInitialBillDocumentType";

const SubBill = lazyLoadWithRetry(() => import("entity/bill/SubBill/SubBill"));
const Bill = lazyLoadWithRetry(() => import("./Bill"));

interface IBillRouteParams {
    id: string;
    jobId: string;
    lienWaiver?: string;
}

export const RouteBill = (
    props: Omit<IBillProps, "handler" | "id" | "jobId" | "history" | "location" | "match">,
) => {
    return (
        <RouteRelative
            path={routes.bill.details}
            render={(routeProps: RouteComponentProps<IBillRouteParams>) => {
                const urlParams = routeProps.match.params;
                return (
                    <BTSuspense>
                        <ShowOnPortal
                            builder
                            render={() => {
                                const routeValues = routeProps.match.params;
                                // eslint-disable-next-line deprecate/member-expression
                                const qsvalues = queryString.parse(routeProps.location.search);
                                const purchaseOrderId = parseInt(routeValues.id, 10);
                                const jobId = parseInt(routeValues.jobId, 10);

                                const openToLienWaiver = routeValues.lienWaiver === "lienWaiver";

                                const sendToAccounting =
                                    (typeof qsvalues.sendToAccounting === "string"
                                        ? qsvalues.sendToAccounting
                                        : qsvalues.sendToAccounting?.[0]
                                    )?.toLowerCase() === "true";
                                const billToOwner =
                                    (typeof qsvalues.billToOwner === "string"
                                        ? qsvalues.billToOwner
                                        : qsvalues.billToOwner?.[0]
                                    )?.toLowerCase() === "true";

                                const isFileUploadOpen =
                                    (typeof qsvalues.is_file_upload_open === "string"
                                        ? qsvalues.is_file_upload_open.toLowerCase()
                                        : qsvalues.is_file_upload_open?.[0]
                                    )?.toLowerCase() === "true";

                                const initialDocumentType = getInitialBillDocumentType(
                                    routeProps.location.search,
                                );

                                return (
                                    <Bill
                                        {...props}
                                        {...routeProps}
                                        key={props.onApproveAndNext ? purchaseOrderId : undefined}
                                        id={purchaseOrderId}
                                        jobId={jobId}
                                        openToLienWaiver={openToLienWaiver}
                                        sendToAccounting={sendToAccounting}
                                        billToOwner={billToOwner}
                                        isFileUploadOpen={isFileUploadOpen}
                                        initialDocumentType={initialDocumentType}
                                    />
                                );
                            }}
                        />
                        <ShowOnPortal
                            subs
                            render={() => (
                                <SubBill
                                    id={parseInt(urlParams.id, 10)}
                                    jobId={parseInt(urlParams.jobId, 10)}
                                    {...routeProps}
                                    modalConfig={props.modalConfig}
                                    openedFrom={props.openedFrom}
                                />
                            )}
                        />
                    </BTSuspense>
                );
            }}
        />
    );
};
